import React, { useCallback, useEffect, useMemo } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { ThreeDots } from 'react-loader-spinner';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import { Input } from '../../../components/common-inputs';
import WithError from '../../../components/with-error/WithError';
import SearchInput from '../../../components/common-inputs/SearchInput';
import CommonButton from '../../../components/common-button/CommonButton';
import { getAccessAssistants, getAccessUsers, loadMoreAccessUsers } from '../../../redux/projects/projectsActions';
import AccessContainers from '../../../components/access-containers/AccessContainers';
import { ASSISTANTS_CATEGORY } from '../../../constants';
import MultiselectDropdown from '../../../components/multiselect-dropdown/MultiselectDropdown';

import scss from './ScoutCreation.module.scss';

const ScoutCreationForm = ({ handleSubmit }) => {
  const { t } = useTranslation('projects');
  const { restart, change } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { values } = useFormState();

  const isAccessUsersLoading = useSelector((state) => state.projects.isAccessUsersLoading);
  const isLoading = useSelector((state) => state.projects.isLoading);
  const { accessUsers, totalAccessUsers, accessAssistants } = useSelector((state) => state.projects);

  const inactiveUsers = useMemo(
    () => accessUsers.filter((user) => !values.activeUsers.some((activeUser) => activeUser.id === user.id)),
    [values, accessUsers],
  );

  const handleRestForm = useCallback(() => {
    restart();
    navigate('/projects/create');
  }, [restart, navigate]);

  const handleActiveUser = (user) => {
    change('activeUsers', [...values.activeUsers, user]);
  };

  const handleInactiveUser = ({ id }) => {
    change(
      'activeUsers',
      values.activeUsers.filter((user) => user.id !== id),
    );
  };

  const handleSearchUsers = (newSearch) => {
    change('assignUser', newSearch);
    dispatch(getAccessUsers({ search: newSearch, page: 1 }));
  };

  const handleLoadMoreUsers = (newPage) => {
    dispatch(loadMoreAccessUsers({ search: values.assignUser ?? '', page: newPage }));
  };

  useEffect(() => {
    dispatch(getAccessUsers({ search: '', page: 1 }));
    dispatch(getAccessAssistants());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <form className={scss.form} onSubmit={handleSubmit}>
      <Field name='projectName' autoComplete='project-name'>
        {({ meta, input }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('projectName')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <Input
                type='text'
                size='sm'
                isError={meta.touched && meta.error}
                formProps={input}
                disabled={isLoading}
                autoComplete='project-name'
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='activeAllAssistants'>
        {({ input, meta }) => (
          <WithLabel variant={LABEL_STYLES.ALIGNED} label={`${t('assistants')}:`} isBold extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <MultiselectDropdown
                value={input.value}
                options={accessAssistants[ASSISTANTS_CATEGORY.GENERATE_ALL]}
                onChange={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                placeholder={t('selectAssistants')}
                disableNewOptions
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='activeSelectedAssistants'>
        {({ input, meta }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('generateMoreAssistants')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <MultiselectDropdown
                value={input.value}
                options={accessAssistants[ASSISTANTS_CATEGORY.GENERATE_SELECTED]}
                onChange={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                placeholder={t('selectAssistants')}
                disableNewOptions
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='activeCompressAssistants'>
        {({ input, meta }) => (
          <WithLabel
            variant={LABEL_STYLES.ALIGNED}
            label={`${t('compressAssistants')}:`}
            isBold
            extraClassName={scss.inputRow}>
            <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
              <MultiselectDropdown
                value={input.value}
                options={accessAssistants[ASSISTANTS_CATEGORY.COMPRESS]}
                onChange={input.onChange}
                name={input.name}
                onBlur={input.onBlur}
                onFocus={input.onFocus}
                isError={meta.touched && meta.error}
                placeholder={t('selectAssistants')}
                disableNewOptions
              />
            </WithError>
          </WithLabel>
        )}
      </Field>

      <Field name='assignUser'>
        {({ input }) => (
          <WithLabel label={t('assignUser')} variant={LABEL_STYLES.ALIGNED} isBold extraClassName={scss.inputRow}>
            <SearchInput
              value={input.value}
              onChange={handleSearchUsers}
              name={input.name}
              size='sm'
              disabled={isLoading}
            />
          </WithLabel>
        )}
      </Field>

      <Field name='activeUsers'>
        {({ input, meta }) => (
          <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)}>
            <AccessContainers
              onInactive={handleInactiveUser}
              onActive={handleActiveUser}
              inactive={inactiveUsers}
              active={input.value}
              disabled={isLoading}
              isLoading={isAccessUsersLoading}
              searchStr={values.assignUser}
              onLoadMore={handleLoadMoreUsers}
              totalElements={totalAccessUsers}
              inactiveLoadedSize={accessUsers.length}
            />
          </WithError>
        )}
      </Field>

      <div className={scss.actions}>
        <CommonButton type='submit' styleColor='primary' onClick={handleSubmit} maxWidth={100} disabled={isLoading}>
          {isLoading ? <ThreeDots height={15} width={40} /> : null}
          {t('create')}
        </CommonButton>
        <CommonButton type='button' styleColor='secondary' onClick={handleRestForm} maxWidth={100} disabled={isLoading}>
          {t('cancel')}
        </CommonButton>
      </div>
    </form>
  );
};

export default ScoutCreationForm;
